import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Server, Shield, Zap, Code2, Star, BellRing, ClipboardList, Clock, Users, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const HowWeWork = () => {
  const strategicPillars = [
    {
      Icon: Server,
      subtitle: 'Systems that scale with your business',
      title: 'Your product handles growth without a rebuild',
      description: 'Because we designed for it from the start. Intentional architecture means you add features without untangling what you already built.',
    },
    {
      Icon: Shield,
      subtitle: 'Secure foundations from day one',
      title: 'Security built in, not bolted on',
      description: 'You\'re never scrambling to fix compliance before a deal closes. HIPAA, SOC 2, data privacy — addressed from the beginning, not as an afterthought.',
    },
    {
      Icon: Zap,
      subtitle: 'Infrastructure that enables progress',
      title: 'You ship features without waiting months for architecture',
      description: 'Deliberate infrastructure decisions create momentum. No pausing the roadmap because the foundation can\'t support what you\'re trying to build.',
    },
    {
      Icon: Code2,
      subtitle: 'You own 100% of your code',
      title: 'You can take it anywhere, anytime',
      description: 'Hire your own team. Switch providers. Bring it in-house. Complete code ownership with no lock-in — the work is always yours.',
    },
    {
      Icon: Star,
      subtitle: 'Architecture that reduces future rework',
      title: 'Decisions that don\'t cost you later',
      description: 'We make technical choices that minimize costly refactors. Your system grows with your business — not against it.',
    },
  ];

  const principles = [
    {
      Icon: BellRing,
      subtitle: 'We reduce ambiguity before committing engineering time',
      title: 'We Keep You Informed',
      description: 'Every Friday, you get an update: what we built this week, what\'s next, and any concerns. No disappearing for months.',
    },
    {
      Icon: ClipboardList,
      subtitle: 'We clarify ideas in writing before building',
      title: 'We Plan Before We Code',
      description: 'You receive written plans you can review, not just "trust us." Every decision is documented and explained.',
    },
    {
      Icon: Clock,
      subtitle: 'We prioritize progress over calendar noise',
      title: 'We Respect Your Time',
      description: 'One 30-minute check-in per week, not daily meetings. We work, you review progress at your convenience.',
    },
    {
      Icon: Users,
      subtitle: 'Work happens deliberately, not reactively',
      title: 'Work on Your Schedule',
      description: 'Read updates and provide feedback when it\'s convenient for you. No pressure to drop everything for a call.',
    },
  ];

  const caseStudies = [
    {
      tag: 'Knowledge Management Platform',
      problem: 'Founder had years of scattered documents, notes, and resources. Finding information took hours, causing missed deadlines and constant frustration. No clear technical path forward.',
      outcome: 'Built custom dashboard with Glean-powered AI chatbot. Plain English search across all documents. Instant answers in seconds, not hours.',
      stats: [{ val: '90%', lbl: 'Faster retrieval' }, { val: '5 sec', lbl: 'Avg search time' }],
    },
    {
      tag: 'Developer Community Platform',
      problem: 'Founder wanted marketplace for developers to connect and collaborate on passion projects. Had vision but no technical background to execute.',
      outcome: 'Complete portal with developer profiles, interest matching, project boards, and messaging. Every decision explained in plain English.',
      stats: [{ val: '200+', lbl: 'Active developers' }, { val: '8 wks', lbl: 'Idea to launch' }],
    },
    {
      tag: 'Health & Wellness App Rescue',
      problem: 'Founder lost access to MVP when developer disappeared. Had paying customers but couldn\'t update app or fix bugs. Complete uncertainty about how to recover.',
      outcome: 'Rebuilt from scratch with proper documentation. Restored code ownership, added requested features, provided training for future development.',
      stats: [{ val: '6 wks', lbl: 'Rebuilt & deployed' }, { val: '100%', lbl: 'Code ownership' }],
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>How We Work — BrightWings</title>
        <meta name="description" content="Clear scope before code, weekly updates, and a process you can follow without a technical background. Here's exactly how working with BrightWings works." />
        <link rel="canonical" href="https://brightwings.io/how-we-work" />
        <meta property="og:url" content="https://brightwings.io/how-we-work" />
        <meta property="og:title" content="How We Work — BrightWings" />
        <meta property="og:description" content="Clear scope before code, weekly updates, and a process you can follow without a technical background. Here's exactly how working with BrightWings works." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How We Work — BrightWings" />
        <meta name="twitter:description" content="Clear scope before code, weekly updates, and a process you can follow without a technical background. Here's exactly how working with BrightWings works." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">How We Work</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              You shouldn't have to guess what's happening with <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>your build.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto' }}>
              We work the way founders actually need: clear scope before code, weekly updates, and a process you can follow without a technical background.
            </p>
          </div>
        </div>
      </section>

      {/* Strategic Pillars */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>What We Focus On For You</h2>
            <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)' }}>Five things we don't compromise on, regardless of timeline or budget.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {strategicPillars.map((pillar, i) => (
              <div key={i} className="bw-feature-card">
                <div className="flex items-start gap-4">
                  <div className="bw-feature-icon flex-shrink-0" style={{ marginBottom: 0 }}>
                    <pillar.Icon size={20} color="var(--bw-blue)" />
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{pillar.subtitle}</p>
                    <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '8px' }}>{pillar.title}</h3>
                    <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{pillar.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>How We Operate</h2>
            <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)' }}>These aren't values on a wall. They're decisions we make on every project.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, i) => (
              <div key={i} className="bw-card">
                <div className="bw-card-body">
                  <div className="flex items-center gap-3 mb-12">
                    <div className="bw-feature-icon" style={{ marginBottom: 0, flexShrink: 0 }}>
                      <principle.Icon size={18} color="var(--bw-blue)" />
                    </div>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{principle.subtitle}</p>
                  </div>
                  <h3 style={{ ...hStyle, fontSize: '20px', marginBottom: '10px' }}>{principle.title}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{principle.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>Our Work</h2>
            <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', maxWidth: '480px', margin: '0 auto' }}>
              Examples of different engagement types: blueprint only, advisory, and full builds.
            </p>
          </div>
          <div className="space-y-8">
            {caseStudies.map((cs, i) => (
              <div key={i} className="bw-card">
                <div className="bw-card-header">
                  <span className="bw-tag-soft">{cs.tag}</span>
                </div>
                <div className="bw-card-body">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Initial Ambiguity</p>
                      <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{cs.problem}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Clarity → Execution → Outcome</p>
                      <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px', marginBottom: '20px' }}>{cs.outcome}</p>
                      <div className="flex gap-6">
                        {cs.stats.map((stat, j) => (
                          <div key={j}>
                            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '24px', color: 'var(--bw-blue)', letterSpacing: '-0.02em' }}>{stat.val}</div>
                            <div style={{ fontSize: '12px', color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)' }}>{stat.lbl}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>Ready to Start?</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Begin with clarity. Get a complete Technical Blueprint that defines your architecture, scope, and implementation roadmap. Then choose: take it elsewhere, add advisory support, or hire us to build.
          </p>
          <Link to="/technical-blueprint" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Start with a Technical Blueprint
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowWeWork;
